import { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { projectsApi, usersApi, manpowerApi, documentsApi, type DocVersion } from '@/lib/apiClient';
import { fmtDateTime } from '@/lib/dates';
import { Button, Modal, Combobox } from '@/components';
import { fmtDate } from '@/lib/dates';
import { GraphConsentError, uploadToFolderId } from '@/lib/graphUpload';
import {
  resolveJobFolder, listChildren, createFolder, createFolders, APP_FOLDERS,
  STANDARD_FOLDERS_CONFIG_KEY, parseStandardFolders, isAppFolder,
  folderExists, foldersToCreate, isFolderChecked,
  type DriveEntry, type JobFolderHandle,
} from '@/lib/graphDocuments';

// mp_config key holding the FY jobs parent folder (e.g. the "2026" folder in
// the EastWestbrookJobs site). Job folders are created under it as
// "{jobNumber} - {job name}". Updated yearly when the next FY folder exists.
const JOBS_PARENT_CONFIG_KEY = 'docsJobsParentUrl';

// Documents (CMS expansion): Procore-style file management over the
// SharePoint Job Folder. LIVE browse with the signed-in user's delegated
// token — the tab is a window into the real folder (including files added
// straight in SharePoint), never a database mirror. The tracker stores
// nothing here in PR 1; version chains land in PR 2.

const GRAPH_SCOPES = ['Files.ReadWrite.All'];

const css = `
  .doc-two { display: flex; gap: var(--ew-s-4); align-items: flex-start; }
  .doc-tree { flex: 0 0 240px; }
  .doc-list { flex: 1; min-width: 0; }
  @media (max-width: 900px) { .doc-two { flex-direction: column; } .doc-tree { flex: 1; width: 100%; } }
  .doc-folder {
    display: flex; align-items: center; gap: 8px; width: 100%; text-align: left;
    padding: 8px 12px; border: 0; background: none; cursor: pointer;
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1.3 var(--ew-font); color: var(--ew-n-700);
    border-left: 3px solid transparent;
  }
  .doc-folder:hover { background: var(--ew-n-50); }
  .doc-folder.active { background: var(--ew-green-50); color: var(--ew-green-900); font-weight: var(--ew-fw-semibold); border-left-color: var(--ew-green); }
  .doc-folder .n { margin-left: auto; font-variant-numeric: tabular-nums; color: var(--ew-n-400); font-size: 12px; }
  /* Fixed grid columns so Updated By / Date align regardless of name length. */
  .doc-row, .doc-head {
    display: grid; grid-template-columns: 34px minmax(0, 1fr) 128px 150px 92px;
    gap: 12px; align-items: center; padding: 10px var(--ew-s-4);
    border-bottom: 1px solid var(--ew-n-100);
  }
  .doc-vchip {
    display: inline-block; border-radius: var(--ew-r-sm); padding: 1px 7px;
    font: var(--ew-fw-semibold) 11px/1.6 var(--ew-font); font-variant-numeric: tabular-nums;
    background: var(--ew-n-100); color: var(--ew-n-600);
  }
  .doc-vtag { font: var(--ew-fw-semibold) 10px/1.6 var(--ew-font); border-radius: var(--ew-r-pill); padding: 1px 8px; margin-left: 6px; }
  .doc-vtag.cur { background: var(--ew-green-50); color: var(--ew-green-700); }
  .doc-vtag.sup { background: var(--ew-n-100); color: var(--ew-n-500); }
  .doc-vbtn {
    border: 0; background: none; cursor: pointer; margin-left: 6px;
    font: var(--ew-fw-medium) 11px/1.6 var(--ew-font); color: var(--ew-quality);
  }
  .doc-hist { background: var(--ew-n-50); border-bottom: 1px solid var(--ew-n-100); padding: 8px var(--ew-s-4) 8px 62px; }
  .doc-hist-row { display: flex; align-items: center; gap: 10px; padding: 6px 0; font-size: 12.5px; flex-wrap: wrap; }
  .doc-head {
    padding: 9px var(--ew-s-4); font: var(--ew-fw-semibold) 11px/1 var(--ew-font);
    letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-500);
  }
  .doc-row { cursor: pointer; }
  .doc-row:last-child { border-bottom: 0; }
  .doc-row:hover { background: var(--ew-n-50); }
  .doc-icon {
    flex: 0 0 34px; height: 34px; border-radius: var(--ew-r-md); display: flex; align-items: center;
    justify-content: center; font: var(--ew-fw-semibold) 10px/1 var(--ew-font); letter-spacing: .02em;
  }
  .doc-crumb { font: var(--ew-fw-medium) var(--ew-fs-body-sm) var(--ew-font); color: var(--ew-n-500); }
  .doc-crumb button { border: 0; background: none; cursor: pointer; color: var(--ew-green-700); font: inherit; padding: 0; }
`;

// File-type icon tint by extension (subset — everything else gets neutral).
function extOf(name: string): string {
  const m = /\.([a-z0-9]+)$/i.exec(name);
  return m ? m[1].toUpperCase() : '';
}
const EXT_TINT: Record<string, { bg: string; fg: string }> = {
  PDF:  { bg: 'var(--ew-danger-50)',      fg: 'var(--ew-danger-700)' },
  DOCX: { bg: 'var(--ew-quality-50)',     fg: 'var(--ew-quality-700)' },
  DOC:  { bg: 'var(--ew-quality-50)',     fg: 'var(--ew-quality-700)' },
  XLSX: { bg: 'var(--ew-green-50)',       fg: 'var(--ew-green-700)' },
  XLS:  { bg: 'var(--ew-green-50)',       fg: 'var(--ew-green-700)' },
  DWG:  { bg: 'var(--ew-performance-50)', fg: 'var(--ew-performance-700)' },
};
function fmtSize(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentsTab({ projectId }: { projectId: number }) {
  const { instance, accounts } = useMsal();
  const qc = useQueryClient();

  const getToken = async (): Promise<string> => {
    try {
      const r = await instance.acquireTokenSilent({ scopes: GRAPH_SCOPES, account: accounts[0] });
      return r.accessToken;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        const r = await instance.acquireTokenPopup({ scopes: GRAPH_SCOPES });
        return r.accessToken;
      }
      throw new GraphConsentError('Graph consent required');
    }
  };

  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId) as Promise<{ jobNumber: string; title: string; jobFolderUrl: string | null }>,
    staleTime: 60_000,
  });
  const jobFolderUrl = projectQuery.data?.jobFolderUrl ?? null;

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  const canEdit = (me?.roles.includes('Admin') ?? false) || (me?.permissions.includes('projects.edit') ?? false);

  // Resolve the Job Folder once, then browse by (drive, folder) ids. Path is a
  // breadcrumb stack of visited folders — real Job Folders nest arbitrarily.
  const handleQuery = useQuery<JobFolderHandle, Error>({
    queryKey: ['doc-handle', projectId, jobFolderUrl],
    queryFn: () => resolveJobFolder(getToken, jobFolderUrl!),
    enabled: !!jobFolderUrl,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  const handle = handleQuery.data;

  const [path, setPath] = useState<{ id: string; name: string }[]>([]);
  const current = path.length ? path[path.length - 1] : null;
  const currentId = current?.id ?? handle?.folderId ?? null;

  // Root listing feeds the tree; current listing feeds the file pane.
  const rootQuery = useQuery<DriveEntry[], Error>({
    queryKey: ['doc-children', projectId, handle?.folderId],
    queryFn: () => listChildren(getToken, handle!.driveId, handle!.folderId),
    enabled: !!handle,
    staleTime: 30_000,
    retry: false,
  });
  const listQuery = useQuery<DriveEntry[], Error>({
    queryKey: ['doc-children', projectId, currentId],
    queryFn: () => listChildren(getToken, handle!.driveId, currentId!),
    enabled: !!handle && !!currentId,
    staleTime: 30_000,
    retry: false,
  });

  // Version chains (PR 2): overlay app-tracked supersede metadata onto the
  // live listing by driveItem id. Superseded files stay in SharePoint but
  // hide from the list by default.
  const versionsQuery = useQuery({
    queryKey: ['doc-versions', projectId],
    queryFn: () => documentsApi.versions(projectId),
    staleTime: 30_000,
  });
  const vByItem = useMemo(() => {
    const m = new Map<string, DocVersion>();
    for (const v of versionsQuery.data ?? []) m.set(v.driveItemId, v);
    return m;
  }, [versionsQuery.data]);
  const chains = useMemo(() => {
    const m = new Map<number, DocVersion[]>();
    for (const v of versionsQuery.data ?? []) {
      const list = m.get(v.chainId) ?? [];
      list.push(v);
      m.set(v.chainId, list);
    }
    for (const list of m.values()) list.sort((a, b) => b.version - a.version);
    return m;
  }, [versionsQuery.data]);
  const [showSuperseded, setShowSuperseded] = useState(false);
  const [expandedChain, setExpandedChain] = useState<number | null>(null);

  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const entries = useMemo(() => {
    let all = listQuery.data ?? [];
    if (!showSuperseded) {
      all = all.filter((e) => {
        const v = vByItem.get(e.id);
        return !v || v.isCurrent;
      });
    }
    return q === '' ? all : all.filter((e) => e.name.toLowerCase().includes(q));
  }, [listQuery.data, q, vByItem, showSuperseded]);

  const rootFolders = (rootQuery.data ?? []).filter((e) => e.isFolder);
  const enterFolder = (e: DriveEntry) => { setPath((p) => [...p, { id: e.id, name: e.name }]); setSearch(''); };
  const jumpTo = (idx: number) => { setPath((p) => p.slice(0, idx)); setSearch(''); };

  const [showUpload, setShowUpload] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const invalidateFolder = (folderId: string | null) => {
    qc.invalidateQueries({ queryKey: ['doc-children', projectId, folderId] });
    qc.invalidateQueries({ queryKey: ['doc-children', projectId, handle?.folderId] });
  };

  // ── No folder linked: one-click create under the FY jobs parent, or link
  //    manually in Job Info. Parent URL lives in config (changes each FY). ──
  const configQuery = useQuery({ queryKey: ['manpower', 'config'], queryFn: manpowerApi.config, staleTime: 60_000 });
  const appConfig = configQuery.data;
  const jobsParentUrl = appConfig?.[JOBS_PARENT_CONFIG_KEY] ?? null;

  // ⚠️ The question is "do we HAVE config", not "did the last fetch succeed".
  // They differ: a background refetch that fails keeps the cached data and
  // still flips isSuccess to false (pinned in queryStaleness.spec.ts).
  //
  // Undefined is the case that must block. It means we never held config at
  // all, and parseStandardFolders answers that with the COMPILED DEFAULTS —
  // so a PM would be offered a list that is not what the database says, and a
  // folder created in SharePoint is not something the tracker can take back.
  //
  // A stale-but-real cache is allowed through deliberately. It is stale by the
  // same seconds as data sitting inside staleTime that simply has not
  // refetched, and blocking on it would disable a working action on a network
  // blip while doing nothing about that identical, error-free window.
  const configLoaded = appConfig !== undefined;

  // The standard taxonomy is admin-editable (Admin → Config).
  // ⚠️ App-managed names are filtered OUT rather than trusted away. Admin →
  // Config refuses to save one, but the row is also reachable by hand in SQL,
  // and both lists get concatenated below — an overlap would render the folder
  // twice (two rows sharing one checkbox) and create it twice.
  const standardFolders = useMemo(
    () => parseStandardFolders(appConfig?.[STANDARD_FOLDERS_CONFIG_KEY]).filter((n) => !isAppFolder(n)),
    [appConfig],
  );
  const [showCreate, setShowCreate] = useState(false);

  if (projectQuery.data && !jobFolderUrl) {
    return (
      <div className="pw-card" style={{ padding: 'var(--ew-s-6)', textAlign: 'center' }}>
        <style>{css}</style>
        <div style={{ font: 'var(--ew-fw-semibold) 15px var(--ew-font)', marginBottom: 6 }}>No SharePoint Job Folder linked</div>
        <div style={{ color: 'var(--ew-n-500)', fontSize: 13, maxWidth: 460, margin: '0 auto 14px' }}>
          Documents live in the project's SharePoint Job Folder. Create it here in one click, or
          link an existing folder's URL in Overview → Job Info.
        </div>
        {/* This path lays down the standard subfolders too, so it carries the
            same gate. `jobsParentUrl` being truthy already implies configLoaded
            today, but that is the kind of implicit coupling a later change
            quietly breaks — a fallback for docsJobsParentUrl would do it — so
            the gate is stated rather than inferred. */}
        {canEdit && configLoaded && jobsParentUrl ? (
          <Button onClick={() => setShowCreate(true)}>Create Job Folder</Button>
        ) : canEdit ? (
          <div style={{ fontSize: 12, color: 'var(--ew-n-400)' }}>
            One-click create needs the FY jobs parent folder configured (config key <code>{JOBS_PARENT_CONFIG_KEY}</code>) — ask an admin.
          </div>
        ) : null}
        {showCreate && configLoaded && jobsParentUrl ? (
          <CreateJobFolderModal
            projectId={projectId}
            jobNumber={projectQuery.data.jobNumber}
            title={projectQuery.data.title}
            parentUrl={jobsParentUrl}
            standardFolders={standardFolders}
            getToken={getToken}
            onClose={() => setShowCreate(false)}
            onDone={() => { setShowCreate(false); qc.invalidateQueries({ queryKey: ['project', projectId] }); }}
          />
        ) : null}
      </div>
    );
  }

  const loadError = handleQuery.error ?? rootQuery.error;

  return (
    <>
      <style>{css}</style>

      {/* ── SharePoint sync banner ── */}
      <div style={{ background: '#fff', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '12px var(--ew-s-4)', marginBottom: 'var(--ew-s-4)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ font: 'var(--ew-fw-semibold) 11px/1 var(--ew-font)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ew-quality)' }}>◇ Synced to SharePoint Job Folder</span>
        <span style={{ font: 'var(--ew-fw-regular) 12px var(--ew-font)', color: 'var(--ew-n-500)' }}>
          SharePoint is the source of truth — the tracker stores links + metadata; files open in SharePoint.
        </span>
        {jobFolderUrl ? (
          <a href={jobFolderUrl} target="_blank" rel="noreferrer" style={{ marginLeft: 'auto', color: 'var(--ew-green-700)', fontWeight: 600, fontSize: 13, textDecoration: 'none' }}>
            Open Job Folder ↗
          </a>
        ) : null}
      </div>

      {/* ── Header row ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 'var(--ew-s-4)', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Documents</h2>
        <span style={{ fontSize: 12, color: 'var(--ew-green-700)', fontWeight: 600 }}>
          🛡 Version control on — superseded files are hidden by default
        </span>
        <button onClick={() => setShowSuperseded((v) => !v)}
          style={{ border: 0, background: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--ew-n-500)', textDecoration: 'underline', padding: 0 }}>
          {showSuperseded ? 'Hide superseded' : 'Show superseded'}
        </button>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {/* Held until config has actually been loaded — see configLoaded.
              Offering this on the compiled fallback would create folders an
              admin may have removed, and SharePoint folders are not ours to
              undo. A stale cache still opens it, with the note below saying so. */}
          {canEdit && handle && rootQuery.data ? (
            <Button variant="secondary" size="sm"
              isLoading={configQuery.isPending}
              disabled={!configLoaded}
              title={configQuery.isError && !configLoaded
                ? "Couldn't load the standard folder list — reload the page to try again."
                : undefined}
              onClick={() => setShowSetup(true)}>
              Set up standard folders
            </Button>
          ) : null}
          {canEdit && handle && rootQuery.data && configQuery.isError ? (
            <span style={{ alignSelf: 'center', fontSize: 11.5, color: 'var(--ew-n-500)' }}>
              {configLoaded
                ? "Couldn't refresh the standard folder list — showing the last one loaded"
                : 'Standard folder list unavailable'}
            </span>
          ) : null}
          {canEdit && handle ? (
            <Button size="sm" onClick={() => setShowUpload(true)}>Upload</Button>
          ) : null}
        </span>
      </div>

      {loadError ? (
        <div className="pw-card" style={{ padding: 'var(--ew-s-5)' }}>
          <div style={{ font: 'var(--ew-fw-semibold) 14px var(--ew-font)', marginBottom: 4 }}>
            {loadError instanceof GraphConsentError ? "Couldn't sign in to SharePoint" : "Couldn't open the Job Folder"}
          </div>
          <div style={{ color: 'var(--ew-n-500)', fontSize: 13 }}>
            {loadError instanceof GraphConsentError
              ? 'Your Microsoft session declined the SharePoint permission — reload and accept the prompt.'
              : `${loadError.message} — you may not have access to this Job Folder yet; ask the PM to share it with you.`}
          </div>
        </div>
      ) : (
        <div className="doc-two">
          {/* ── Folder tree ── */}
          <div className="pw-card doc-tree">
            <h3 className="pw-card-hd">Job Folder</h3>
            <div style={{ padding: '6px 0' }}>
              <button className={`doc-folder${path.length === 0 ? ' active' : ''}`} onClick={() => jumpTo(0)}>
                📁 <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Job Folder (root)</span>
              </button>
              {rootFolders.map((f) => (
                <button
                  key={f.id}
                  className={`doc-folder${path[0]?.id === f.id ? ' active' : ''}`}
                  onClick={() => { setPath([{ id: f.id, name: f.name }]); setSearch(''); }}
                >
                  📁 <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                  {f.childCount != null ? <span className="n">{f.childCount}</span> : null}
                </button>
              ))}
              {rootQuery.isLoading ? <div style={{ padding: '8px 12px', color: 'var(--ew-n-400)', fontSize: 12 }}>Loading folders…</div> : null}
            </div>
          </div>

          {/* ── File list ── */}
          <div className="pw-card doc-list">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px var(--ew-s-4)', borderBottom: '1px solid var(--ew-n-100)', flexWrap: 'wrap' }}>
              <span className="doc-crumb">
                <button onClick={() => jumpTo(0)}>Job Folder</button>
                {path.map((p, i) => (
                  <span key={p.id}> ▸ {i === path.length - 1 ? <b style={{ color: 'var(--ew-dark)' }}>{p.name}</b> : <button onClick={() => jumpTo(i + 1)}>{p.name}</button>}</span>
                ))}
              </span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search this folder…"
                style={{ marginLeft: 'auto', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '6px 10px', font: 'var(--ew-fw-regular) 12px var(--ew-font)', width: 220 }}
              />
            </div>

            {listQuery.isLoading ? (
              <div style={{ padding: 'var(--ew-s-5)', color: 'var(--ew-n-500)', fontSize: 13 }}>Reading the folder from SharePoint…</div>
            ) : entries.length === 0 ? (
              <div style={{ padding: 'var(--ew-s-5)', color: 'var(--ew-n-500)', fontSize: 13 }}>
                {q !== '' ? `Nothing here matches "${search.trim()}".` : 'This folder is empty.'}
              </div>
            ) : (
              <>
                <div className="doc-head">
                  <span />
                  <span>Name</span>
                  <span>Version</span>
                  <span>Updated By</span>
                  <span>Date</span>
                </div>
                {entries.map((e) => {
                  const ext = e.isFolder ? '' : extOf(e.name);
                  const tint = EXT_TINT[ext] ?? { bg: 'var(--ew-n-100)', fg: 'var(--ew-n-600)' };
                  const v = e.isFolder ? undefined : vByItem.get(e.id);
                  const chain = v ? chains.get(v.chainId) : undefined;
                  const expanded = v != null && expandedChain === v.chainId;
                  return (
                    <div key={e.id}>
                      {/* Whole row is the action: folders drill in, files open. */}
                      <div className="doc-row"
                        onClick={e.isFolder ? () => enterFolder(e) : () => window.open(e.webUrl, '_blank', 'noopener')}>
                        <span className="doc-icon" style={e.isFolder ? { background: 'var(--ew-n-100)', fontSize: 15 } : { background: tint.bg, color: tint.fg }}>
                          {e.isFolder ? '📁' : (ext || 'FILE')}
                        </span>
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: 'block', fontWeight: 500, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name}</span>
                          <span style={{ fontSize: 11.5, color: 'var(--ew-n-400)' }}>
                            {e.isFolder
                              ? `${e.childCount ?? 0} item${e.childCount === 1 ? '' : 's'}`
                              : [ext, fmtSize(e.size)].filter(Boolean).join(' · ')}
                          </span>
                        </span>
                        <span onClick={(ev) => ev.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                          {e.isFolder ? null : v && v.chainSize > 1 ? (
                            <>
                              <span className="doc-vchip">v{v.version}</span>
                              {v.isCurrent
                                ? <button className="doc-vbtn" onClick={() => setExpandedChain(expanded ? null : v.chainId)}>{v.chainSize} versions {expanded ? '▴' : '▾'}</button>
                                : <span className="doc-vtag sup">Superseded</span>}
                            </>
                          ) : (
                            <>
                              <span className="doc-vchip">v1</span>
                              <span className="doc-vtag cur">Current</span>
                            </>
                          )}
                        </span>
                        <span style={{ fontSize: 12.5, color: 'var(--ew-n-600)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.lastModifiedBy ?? '—'}</span>
                        <span style={{ fontSize: 12, color: 'var(--ew-n-500)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{e.lastModifiedDateTime ? fmtDate(e.lastModifiedDateTime) : '—'}</span>
                      </div>
                      {expanded && chain ? (
                        <div className="doc-hist">
                          <div style={{ font: 'var(--ew-fw-semibold) 10px/1 var(--ew-font)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ew-n-500)', margin: '4px 0 2px' }}>Revision History</div>
                          {chain.map((cv) => (
                            <div key={cv.id} className="doc-hist-row">
                              <span className="doc-vchip">v{cv.version}</span>
                              <span style={{ color: 'var(--ew-n-600)' }}>{fmtDateTime(cv.uploadedAt)}{cv.uploadedBy ? ` · ${cv.uploadedBy}` : ''}</span>
                              {cv.note ? <span style={{ color: 'var(--ew-n-500)' }}>— {cv.note}</span> : null}
                              <span className={`doc-vtag ${cv.isCurrent ? 'cur' : 'sup'}`}>{cv.isCurrent ? 'Current' : 'Superseded'}</span>
                              <a href={cv.webUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--ew-quality)', textDecoration: 'none', fontSize: 12 }}>open</a>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}

      {showUpload && handle ? (
        <UploadModal
          projectId={projectId}
          jobNumber={projectQuery.data?.jobNumber ?? ''}
          handle={handle}
          getToken={getToken}
          folders={rootFolders}
          defaultFolder={path[0] ?? null}
          currentFolder={current}
          folderFiles={(listQuery.data ?? []).filter((e) => !e.isFolder)}
          vByItem={vByItem}
          onDone={(folderId) => {
            setShowUpload(false);
            invalidateFolder(folderId);
            qc.invalidateQueries({ queryKey: ['doc-versions', projectId] });
          }}
          onClose={() => setShowUpload(false)}
        />
      ) : null}

      {showSetup && handle && configLoaded ? (
        <SetupFoldersModal
          standardFolders={standardFolders}
          existing={rootFolders.map((f) => f.name)}
          onCreate={async (names) => {
            const r = await createFolders(getToken, handle.driveId, handle.folderId, names);
            invalidateFolder(handle.folderId);
            return r;
          }}
          onClose={() => setShowSetup(false)}
        />
      ) : null}
    </>
  );
}

// ─── Upload modal — "New file" registers a v1 chain; "New version of a file"
//     supersedes an existing one (untracked files get their live SharePoint
//     item registered as v1 first, so history is honest either way). ─────────
function UploadModal({ projectId, jobNumber, handle, getToken, folders, defaultFolder, currentFolder, folderFiles, vByItem, onDone, onClose }: {
  projectId: number;
  jobNumber: string;
  handle: JobFolderHandle;
  getToken: () => Promise<string>;
  folders: DriveEntry[];
  defaultFolder: { id: string; name: string } | null;
  currentFolder: { id: string; name: string } | null;
  folderFiles: DriveEntry[];
  vByItem: Map<string, DocVersion>;
  onDone: (folderId: string) => void;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<'new' | 'version'>('new');
  const [replaceItemId, setReplaceItemId] = useState('');
  const [note, setNote] = useState('');
  // Destination defaults to the folder being viewed (deep paths included).
  const dest0 = currentFolder ?? defaultFolder;
  const [destId, setDestId] = useState<string>(dest0?.id ?? handle.folderId);
  const [file, setFile] = useState<File | null>(null);
  // ENFORCED naming convention (product owner: a hint gets ignored): the name is BUILT
  // from structured parts — JobNo + date are locked, the user supplies
  // Doc # / short description / status. No free-text filename.
  const [docNo, setDocNo] = useState('');
  const [shortDesc, setShortDesc] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const pick = (f: File | null) => {
    setFile(f);
    // Seed the short description from the original name (sanitized).
    if (f && !shortDesc) setShortDesc(f.name.replace(/\.[a-z0-9]+$/i, ''));
  };

  const clean = {
    docNo: docNo.trim().replace(/[^A-Za-z0-9-]/g, ''),
    shortDesc: shortDesc.replace(/[^A-Za-z0-9]/g, '').slice(0, 30),
    status: status.trim().toUpperCase().replace(/[^A-Z0-9-]/g, ''),
  };
  const ext = file ? (/\.([a-z0-9]+)$/i.exec(file.name)?.[1] ?? 'bin') : '';
  const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
  const builtName = file && clean.docNo && clean.shortDesc && clean.status
    ? `${jobNumber}_${clean.docNo}_${clean.shortDesc}_${clean.status}_${today}.${ext}`
    : null;

  const submit = async () => {
    if (!file) { setError('Choose a file first.'); return; }
    if (!builtName) { setError('Fill in Doc #, description, and status — the file name is built from them.'); return; }
    const target = mode === 'version' ? folderFiles.find((f) => f.id === replaceItemId) : undefined;
    if (mode === 'version' && !target) { setError('Pick which file this replaces.'); return; }
    setBusy(true); setError(null);
    try {
      const token = await getToken();
      const uploaded = await uploadToFolderId({ token, driveId: handle.driveId, folderId: destId, fileName: builtName, file });
      // Register the version chain. The FILE is already safely in SharePoint —
      // a registration failure must not read as "upload failed".
      try {
        const tracked = target ? vByItem.get(target.id) : undefined;
        await documentsApi.registerVersion({
          projectId,
          driveItemId: uploaded.id,
          webUrl: uploaded.webUrl,
          fileName: uploaded.name,
          sizeBytes: uploaded.size,
          folderItemId: destId,
          note: note.trim() || undefined,
          ...(mode === 'version'
            ? tracked
              ? { replaceVersionId: tracked.id }
              : {
                  prior: {
                    driveItemId: target!.id,
                    webUrl: target!.webUrl,
                    fileName: target!.name,
                    lastModifiedBy: target!.lastModifiedBy ?? undefined,
                    lastModifiedDateTime: target!.lastModifiedDateTime ?? undefined,
                  },
                }
            : {}),
        });
      } catch (regErr: any) {
        setError(`The file uploaded to SharePoint, but version tracking failed: ${regErr?.message ?? 'unknown error'}. Close and refresh — the file is safe.`);
        setBusy(false);
        return;
      }
      onDone(destId);
    } catch (e: any) {
      setError(e?.message ?? 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  // The current deep folder may not be a root folder — offer it explicitly.
  const options: { id: string; name: string }[] = [
    { id: handle.folderId, name: 'Job Folder (root)' },
    ...(currentFolder && !folders.some((f) => f.id === currentFolder.id) && currentFolder.id !== handle.folderId
      ? [{ id: currentFolder.id, name: `Current: ${currentFolder.name}` }] : []),
    ...folders.map((f) => ({ id: f.id, name: f.name })),
  ];

  return (
    <Modal isOpen onClose={onClose} title="Upload to Job Folder"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} isLoading={busy}>Upload to Job Folder</Button>
        </>
      }>
      <div style={{ fontSize: 12, color: 'var(--ew-n-500)', marginBottom: 14 }}>
        Writes to the project's SharePoint Job Folder via Microsoft Graph
      </div>
      {error ? <div style={{ marginBottom: 12, padding: '6px 10px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12 }}>{error}</div> : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Segmented: new chain vs superseding an existing file. */}
        <div style={{ display: 'flex', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-pill)', overflow: 'hidden', alignSelf: 'flex-start' }}>
          {(['new', 'version'] as const).map((m) => (
            <button key={m} onClick={() => setMode(m)} style={{
              border: 0, cursor: 'pointer', padding: '7px 16px',
              font: 'var(--ew-fw-medium) 12px/1 var(--ew-font)',
              background: mode === m ? 'var(--ew-dark)' : '#fff',
              color: mode === m ? '#fff' : 'var(--ew-n-600)',
            }}>{m === 'new' ? 'New file' : 'New version of a file'}</button>
          ))}
        </div>

        {mode === 'version' ? (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ font: 'var(--ew-fw-medium) 11px/1 var(--ew-font)', letterSpacing: 'var(--ew-ls-caption)', textTransform: 'uppercase', color: 'var(--ew-n-500)' }}>Replace which file</span>
            <select value={replaceItemId} onChange={(e) => setReplaceItemId(e.target.value)}
              style={{ border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '7px 10px', font: 'var(--ew-fw-regular) 13px var(--ew-font)', background: '#fff' }}>
              <option value="">— pick a file in this folder —</option>
              {folderFiles.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            <span style={{ fontSize: 11.5, color: 'var(--ew-n-400)' }}>
              Uploads as the new Current version; the prior version is kept in revision history.
            </span>
          </label>
        ) : (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ font: 'var(--ew-fw-medium) 11px/1 var(--ew-font)', letterSpacing: 'var(--ew-ls-caption)', textTransform: 'uppercase', color: 'var(--ew-n-500)' }}>Destination folder</span>
            <select value={destId} onChange={(e) => setDestId(e.target.value)}
              style={{ border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '7px 10px', font: 'var(--ew-fw-regular) 13px var(--ew-font)', background: '#fff' }}>
              {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </label>
        )}

        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files?.[0] ?? null); }}
          style={{
            border: '2px dashed var(--ew-n-300)', borderRadius: 'var(--ew-r-md)', padding: '26px 16px',
            textAlign: 'center', cursor: 'pointer', color: 'var(--ew-n-500)', fontSize: 13,
            background: file ? 'var(--ew-green-50)' : '#fff',
          }}>
          {file ? <b style={{ color: 'var(--ew-green-900)' }}>{file.name}</b> : <>Drag a file here, or <b style={{ color: 'var(--ew-green-700)' }}>browse</b></>}
          <div style={{ fontSize: 11.5, color: 'var(--ew-n-400)', marginTop: 4 }}>PDF, DWG, DOCX, XLSX · up to 250 MB</div>
          <input ref={inputRef} type="file" hidden onChange={(e) => pick(e.target.files?.[0] ?? null)} />
        </div>

        {/* Structured name builder — the convention is the only path. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 1fr', gap: 10 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ font: 'var(--ew-fw-medium) 11px/1 var(--ew-font)', letterSpacing: 'var(--ew-ls-caption)', textTransform: 'uppercase', color: 'var(--ew-n-500)' }}>Doc #</span>
            <input value={docNo} onChange={(e) => setDocNo(e.target.value)} placeholder="e.g. CT-001"
              style={{ border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '7px 10px', font: 'var(--ew-fw-regular) 13px var(--ew-font)' }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ font: 'var(--ew-fw-medium) 11px/1 var(--ew-font)', letterSpacing: 'var(--ew-ls-caption)', textTransform: 'uppercase', color: 'var(--ew-n-500)' }}>Short description</span>
            <input value={shortDesc} onChange={(e) => setShortDesc(e.target.value)} placeholder="e.g. Owner Contract"
              style={{ border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '7px 10px', font: 'var(--ew-fw-regular) 13px var(--ew-font)' }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ font: 'var(--ew-fw-medium) 11px/1 var(--ew-font)', letterSpacing: 'var(--ew-ls-caption)', textTransform: 'uppercase', color: 'var(--ew-n-500)' }}>Status</span>
            {/* Shared Combobox, not <datalist> — the native popup is the
                unstylable browser control the product owner rejected on Materials. */}
            <Combobox
              value={status}
              onChange={setStatus}
              options={['DRAFT', 'FOR-REVIEW', 'ISSUED', 'APPROVED', 'EXECUTED', 'FINAL', 'RECORD'].map((s) => ({ value: s }))}
              placeholder="e.g. EXECUTED"
            />
          </label>
        </div>
        <div style={{ padding: '8px 12px', background: 'var(--ew-n-50)', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', font: '12.5px var(--ew-font-mono, ui-monospace, monospace)', color: builtName ? 'var(--ew-dark)' : 'var(--ew-n-400)', overflowWrap: 'anywhere' }}>
          {builtName ?? `${jobNumber || 'JobNo'}_DocNo_ShortDesc_STATUS_${today}${ext ? `.${ext}` : ''}`}
        </div>

        {mode === 'version' ? (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ font: 'var(--ew-fw-medium) 11px/1 var(--ew-font)', letterSpacing: 'var(--ew-ls-caption)', textTransform: 'uppercase', color: 'var(--ew-n-500)' }}>What changed (optional)</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Owner-executed copy replacing the draft"
              style={{ border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '7px 10px', font: 'var(--ew-fw-regular) 13px var(--ew-font)' }} />
          </label>
        ) : null}

        <div style={{ padding: '8px 12px', background: 'var(--ew-quality-50)', border: '1px solid var(--ew-quality-100)', borderRadius: 'var(--ew-r-md)', fontSize: 12, color: 'var(--ew-quality-700)' }}>
          SharePoint is the source of truth — the tracker stores the link + metadata, not the file.
        </div>
      </div>
    </Modal>
  );
}

// ─── Standard-folders setup — checklist, NOT a blind create-all: existing
//     equivalents under different names are the PM's call to skip. ───────────
/** Exported for its spec — see AdminPage's StandardFoldersEditor. */
export function SetupFoldersModal({ standardFolders, existing, onCreate, onClose }: {
  /** The admin-configured standard taxonomy (Admin → Config). */
  standardFolders: string[];
  existing: string[];
  onCreate: (names: string[]) => Promise<{ created: string[]; failed: { name: string; error: string }[] }>;
  onClose: () => void;
}) {
  const ALL = [...standardFolders, ...APP_FOLDERS];
  const existsExact = (n: string) => folderExists(existing, n);
  // ⚠️ ONLY the PM's explicit toggles are state. The default — check it unless
  // it's already there — is derived per render, because `standardFolders` can
  // change after this modal opens (a retried config fetch landing late). A
  // checked-map seeded once at mount left any late-arriving folder rendering
  // unchecked and silently dropped from the create.
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const isChecked = (n: string) => isFolderChecked(existing, overrides, n);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toCreate = foldersToCreate(ALL, existing, overrides);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const r = await onCreate(toCreate);
      if (r.failed.length) setError(r.failed.map((f) => `${f.name}: ${f.error}`).join(' · '));
      else onClose();
    } catch (e: any) {
      setError(e?.message ?? 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Set up standard folders"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} isLoading={busy} disabled={toCreate.length === 0}>
            Create {toCreate.length} folder{toCreate.length === 1 ? '' : 's'}
          </Button>
        </>
      }>
      <div style={{ fontSize: 12.5, color: 'var(--ew-n-500)', marginBottom: 12 }}>
        The standard Job Folder layout. Folders that already exist are checked off; if an existing
        folder covers the same ground under a different name, uncheck the standard one — nothing is
        ever renamed or moved.
      </div>
      {error ? <div style={{ marginBottom: 10, padding: '6px 10px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12 }}>{error}</div> : null}
      {standardFolders.map((n) => <FolderCheckRow key={n} name={n} has={existsExact(n)} checked={isChecked(n)} onChange={(v) => setOverrides((p) => ({ ...p, [n]: v }))} />)}
      <div style={{ font: 'var(--ew-fw-semibold) 10px/1 var(--ew-font)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ew-n-400)', margin: '12px 0 4px' }}>
        App-managed — uploads from the Project Tracker land here (created on first use either way)
      </div>
      {APP_FOLDERS.map((n) => <FolderCheckRow key={n} name={n} has={existsExact(n)} checked={isChecked(n)} onChange={(v) => setOverrides((p) => ({ ...p, [n]: v }))} />)}
    </Modal>
  );
}

function FolderCheckRow({ name, has, checked, onChange }: {
  name: string; has: boolean; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 4px', fontSize: 13.5, color: has ? 'var(--ew-n-400)' : 'var(--ew-dark)' }}>
      {has
        ? <span style={{ color: 'var(--ew-green)', fontWeight: 700 }}>✓</span>
        : <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />}
      📁 {name}
      {has ? <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ew-n-400)' }}>already exists</span> : null}
    </label>
  );
}

// ─── Create Job Folder — one click from "no folder linked" to a working tab.
//     Creates "{jobNumber} - {title}" under the FY jobs parent, optionally
//     lays down the standard subfolders, and saves the URL to the project.
//     Idempotent: an existing same-named folder is linked instead of failing.
function CreateJobFolderModal({ projectId, jobNumber, title, parentUrl, standardFolders, getToken, onClose, onDone }: {
  projectId: number;
  jobNumber: string;
  title: string;
  parentUrl: string;
  /** The admin-configured standard taxonomy (Admin → Config). */
  standardFolders: string[];
  getToken: () => Promise<string>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState(`${jobNumber} - ${title}`.trim());
  const [withStandard, setWithStandard] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim()) { setError('Folder name is required.'); return; }
    setBusy(true); setError(null);
    try {
      const parent = await resolveJobFolder(getToken, parentUrl);
      const folder = await createFolder(getToken, parent.driveId, parent.folderId, name.trim());
      if (withStandard) {
        // Standard taxonomy + the app-managed subfolders (RFIs / Submittals /
        // Daily Log Photos / Drawings) so the folder is complete from day one.
        await createFolders(getToken, parent.driveId, folder.id, [...standardFolders, ...APP_FOLDERS]);
      }
      // Save the link — the same field Job Info edits (partial PATCH).
      await projectsApi.update(projectId, { jobFolderUrl: folder.webUrl });
      onDone();
    } catch (e: any) {
      setError(e?.message ?? 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Create Job Folder"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} isLoading={busy}>Create & link</Button>
        </>
      }>
      <div style={{ fontSize: 12.5, color: 'var(--ew-n-500)', marginBottom: 12, textAlign: 'left' }}>
        Creates the folder in this fiscal year's Jobs library via Microsoft Graph (your own
        SharePoint access), then links it to this project. If a folder with this exact name
        already exists there, it's linked as-is.
      </div>
      {error ? <div style={{ marginBottom: 10, padding: '6px 10px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12, textAlign: 'left' }}>{error}</div> : null}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, textAlign: 'left' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ font: 'var(--ew-fw-medium) 11px/1 var(--ew-font)', letterSpacing: 'var(--ew-ls-caption)', textTransform: 'uppercase', color: 'var(--ew-n-500)' }}>Folder name</span>
          <input value={name} onChange={(e) => setName(e.target.value)}
            style={{ border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '7px 10px', font: 'var(--ew-fw-regular) 13px var(--ew-font)' }} />
          <span style={{ fontSize: 11.5, color: 'var(--ew-n-400)' }}>Convention: job number - job name</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={withStandard} onChange={(e) => setWithStandard(e.target.checked)} />
          Also create the {standardFolders.length} standard subfolders plus the app-managed
          {' '}{APP_FOLDERS.join(' / ')}
        </label>
      </div>
    </Modal>
  );
}
